import { Download, X } from 'lucide-react'
import { toExcalidrawFile, type DiagramScene } from './scene'
import { ExcalidrawCanvas } from './excalidraw-canvas'

/**
 * A canvas, on a conversation.
 *
 * IT TAKES A SCENE, NOT A REQUEST. Three things open it and only one of
 * them is the agent: a diagram it drew, a blank one you started, and a
 * `.excalidraw` file opened out of the workspace. Compiling belongs to
 * whoever has something to compile, so this component only ever places
 * elements on a canvas and offers to save them.
 *
 * IT IS THE EDITOR, not a render. Excalidraw is carried for exactly one
 * reason: the output belongs to the person looking at it. They can drag a
 * box, fix a label the agent got slightly wrong, draw on it and keep it.
 * A picture they could only accept or reject would not have been worth
 * the megabyte.
 */
export function DiagramStage({
  scene,
  onClose,
  onSave,
  saving,
}: {
  scene: DiagramScene
  onClose: () => void
  /** Write it into the organisation's workspace as a `.excalidraw` file. */
  onSave?: (filename: string, contents: string) => void
  saving?: 'saving' | 'saved' | null
}) {
  const filename = `${
    (scene.title ?? 'diagram')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'diagram'
  }.excalidraw`

  return (
    <>
      <header className="flex shrink-0 items-center justify-between gap-2 border-b border-black/5 px-3 py-2 dark:border-white/5">
        <div className="min-w-0">
          <h2 className="truncate text-sm font-medium text-foreground">
            {scene.title ?? 'Diagram'}
          </h2>
          <p className="truncate text-[11px] text-muted-foreground/70">
            Drag it about, change the labels, it is yours
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {onSave ? (
            <button
              type="button"
              aria-label="Save to the workspace"
              title={`Save as ${filename} in the organisation's files`}
              disabled={saving === 'saving'}
              onClick={() => {
                onSave(filename, toExcalidrawFile(scene))
              }}
              className="flex items-center gap-1.5 rounded-lg border border-black/10 px-2 py-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50 dark:border-white/10"
            >
              <Download className="size-3.5" />
              {saving === 'saving' ? 'Saving…' : saving === 'saved' ? 'Saved' : 'Save'}
            </button>
          ) : null}
          <button
            type="button"
            aria-label="Close the diagram"
            onClick={onClose}
            className="rounded p-1.5 text-muted-foreground hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </div>
      </header>
      {/* `flex-1` and `min-h-0`: Excalidraw measures its container and
          draws to fill it, so a parent that sizes to its content gives it
          nothing to draw into. */}
      <div className="min-h-0 flex-1">
        <ExcalidrawCanvas scene={scene} />
      </div>
    </>
  )
}
